import React, { createContext, useContext, useEffect, useMemo, useState, useCallback, useRef } from 'react';
import { get_api_client, getToken, setDefaultStepUpHandler } from '../utils/teamsApi';
import useLocalStorage from '../hooks/useLocalStorage';
import { useMsal } from '@azure/msal-react';
import { jwtDecode } from 'jwt-decode';
import { getJwtExpiryDuration } from '../utils/jwt';
import { scopes } from "@repo/shared/config";
import { Spinner, Button } from '@fluentui/react-components';
import { mutate } from 'swr';
import LoginPanel from '../components/LoginPanel';
import { PolyguardClient } from '@polyguard/sdk';
import { endpoint } from '../utils/utils';
// import { useNavigate } from 'react-router-dom';

const PolyAuthContext = createContext(null);

export const PolyAuthProvider = ({ children, msalInstance, appContext, meetingUUID, requireAccount = false, allowAnonymous = false }) => {
    const [needsClick, setNeedsClick] = useState(false);
    const [originalUserBundle, setOriginalUserBundle] = useLocalStorage('originalUserBundle');
    const [sessionToken, setSessionToken] = useLocalStorage('sessionToken');
    const [verificationJWT, setVerificationJWT] = useLocalStorage('verificationJWT');
    const [userName, setUserName] = useState(null);
    const [accountRoles, setAccountRoles] = useState({});
    const [selectedAccountId, setSelectedAccountId] = useLocalStorage('selectedPolyguardAccountId');
    const [zoomAccountId, setZoomAccountId] = useLocalStorage('ZoomAccountId', 1000000000);
    const [knownAccounts, setKnownAccounts] = useLocalStorage('polyguardKnownAccounts');
    const [msalToken, setMsalToken] = useState(null);
    const [needsAccountSetup, setNeedsAccountSetup] = useState(false);
    const [needsSignup, setNeedsSignup] = useState(false);
    const [needsSync, setNeedsSync] = useState(false);
    const [zoomEnabledAccounts, setZoomEnabledAccounts] = useState([]);
    const [hasZoomApp, setHasZoomApp] = useState(false);
    const [needsZoomReauth, setNeedsZoomReauth] = useState(false);
    const [teamsLinkedAccount, setTeamsLinkedAccount] = useState(null);
    // How the current session was established: 'cert' (verification bundle),
    // 'email' (magic link), or 'sso' (the account's identity provider).
    // Mirrors the session JWT's auth_method claim;
    // older tokens without the claim are cert-backed by construction.
    const [authMethod, setAuthMethod] = useState(null);
    // Derived from the token, not state like authMethod/userName: it gates a
    // route, so effect-state would flash the no-records page on reload.
    const dataSubject = useMemo(() => {
        if (!sessionToken) return false;
        try {
            return !!jwtDecode(sessionToken).data_subject;
        } catch {
            return false;
        }
    }, [sessionToken]);
    // Session subject (poly_user_id claim). Email-only identities that have
    // not yet been adopted under a cert-backed parent carry the reserved
    // "email-x" prefix (backend PolyUser.EMAIL_ONLY_ID_PREFIX); once linked,
    // an email login resolves to the parent's cert id instead.
    const [polyUserId, setPolyUserId] = useState(null);
    // Set when /v2/session/step_up returns 409: the email identity belongs
    // to a different cert-backed user, so elevation is impossible and the
    // only way forward is a full logout + Polyguard login.
    const [stepUpConflict, setStepUpConflict] = useState(false);
    const stepUpRef = useRef(null);
    const stepUpPromiseRef = useRef(null);
    // Custom step-up dialog plumbing. An app that renders its own step-up
    // dialog registers as a UI host; step-up then renders the SDK QR into
    // the dialog's container (target mode) instead of the SDK's own modal.
    const [stepUpPromptOpen, setStepUpPromptOpen] = useState(false);
    const stepUpUiHostsRef = useRef(0);
    const stepUpTargetResolveRef = useRef(null);
    const stepUpCancelRef = useRef(null);
    // const navigate = useNavigate();

    const logout = () => {
        console.log("Logging out");
        setSessionToken(null);
        setUserName(null);
        setAccountRoles({});
        setSelectedAccountId(null);
        setVerificationJWT(null);
        setNeedsSignup(false);
        setOriginalUserBundle(null);
        setAuthMethod(null);
        setPolyUserId(null);
        setStepUpConflict(false);
    };

    // Verify identity via PolyguardClient SDK.
    // HTTP (ticket creation) routes through the app's /api/polyguard Edge proxy
    // so the Polyguard API key stays server-side. The WebSocket upgrade uses
    // `apiServer` directly — it is ticket-authenticated, not API-key-authenticated.
    const verify = async (options = {}) => {
        // The /api/polyguard edge proxy only exists on Vercel deployments. In
        // local dev (`craco start`) there is no edge function, so ticket
        // creation must hit the API host directly — the ticket route needs no
        // API key and backend CORS allows it.
        const defaultProxyUrl = process.env.NODE_ENV === 'development'
            ? `https://${endpoint()}`
            : "/api/polyguard";
        const config = {
            integrationType: "websocket",
            appId: "3",
            proxyUrl: options.proxyUrl || defaultProxyUrl,
            apiServer: options.apiServer || process.env.REACT_APP_API_SERVER || endpoint(),
            requiredProofs: options.requiredProofs || [
                "name",
                "pg_attestation_key_id",
                "pg_photo_verification_url",
                "pg_high_certainty",
                "pg_presence",
                "pg_presence_scoring",
            ],
            scanType: (options.requiredProofs || ["pg_presence"]).some(
                (proof) => proof === "pg_presence" || proof === "pg_presence_scoring"
            ) ? "multi" : "single",
        };
        if (options.linkUuid) config.link_uuid = options.linkUuid;
        const client = new PolyguardClient(config);
        // targetId renders the QR into an existing element (SDK target mode
        // takes an element ID); otherwise the SDK shows its own modal. In
        // target mode SDK failures RESOLVE with {presence:{score,msg}} and
        // no jwt/raw_jwt, so callers must treat a missing bundle as failure.
        const response = await client.verify(options.targetId || null, true);
        const jwt = response?.jwt;
        const jwtData = typeof jwt === 'string' ? JSON.parse(jwt) : jwt;
        const exp = jwtData?.exp;
        const expiryDuration = exp ? Math.max((exp * 1000) - Date.now(), 0) : undefined;
        setVerificationJWT(jwt, expiryDuration);
        return { jwt, raw_jwt: response?.raw_jwt };
    };

    // Exchange user_bundle for session token, store both with matching expiry
    const exchangeForSession = async (rawJwt, targetAccountId = null) => {
        if (!rawJwt) {
            console.error('exchangeForSession called with no user bundle');
            throw new Error('Cannot exchange session: no user bundle provided');
        }
        const effectiveAccountId = targetAccountId || selectedAccountId;
        const payload = { user_bundle: rawJwt };
        if (effectiveAccountId) payload.current_account_id = effectiveAccountId;
        try {
            const response = await api_client.post('/portal_login', payload);
            const expiryDuration = getJwtExpiryDuration(response.data.jwt);
            setSessionToken(response.data.jwt, expiryDuration);
            setOriginalUserBundle(rawJwt, expiryDuration);
            return response.data.jwt;
        } catch (error) {
            // If the stale selectedAccountId is no longer valid for this user,
            // clear it and retry without an account preference
            if (effectiveAccountId && !targetAccountId && error?.response?.status === 401) {
                console.warn('Selected account no longer valid, clearing and retrying without account preference');
                setSelectedAccountId(null);
                const retryResponse = await api_client.post('/portal_login', { user_bundle: rawJwt });
                const expiryDuration = getJwtExpiryDuration(retryResponse.data.jwt);
                setSessionToken(retryResponse.data.jwt, expiryDuration);
                setOriginalUserBundle(rawJwt, expiryDuration);
                return retryResponse.data.jwt;
            }
            throw error;
        }
    };

    // Request a magic-link email. The response is uniform for every address
    // (anti-enumeration), so success only means "if that address is valid,
    // a link is on its way".
    // totp: the caller will finish with an authenticator code, which on a
    // domain that requires SSO asks for the owners' break-glass link instead
    // of the 409 hand-off.
    const requestEmailLogin = async (email, { totp = false } = {}) => {
        const response = await api_client.post('/v2/email/login/request', totp ? { email, totp: true } : { email });
        return response.data;
    };

    // Exchange a magic-link token for an email-assurance session. There is
    // no verification bundle for these sessions. A user who has enrolled an
    // authenticator gets { totp_required, challenge } instead of a jwt;
    // verifyTotpChallenge finishes the sign-in.
    const verifyEmailLogin = async ({ token }) => {
        const response = await api_client.post('/v2/email/login/verify', { token });
        const jwt = response.data.jwt;
        if (jwt) setSessionToken(jwt, getJwtExpiryDuration(jwt));
        return response.data;
    };

    // Second factor: the challenge from verifyEmailLogin plus the current
    // code. 401 totp_invalid for any refusal, 403 sso_required when every
    // account keeps non-owners on its identity provider, 429 when rate limited.
    const verifyTotpChallenge = async ({ challenge, code }) => {
        const response = await api_client.post('/totp/verify', { challenge, code });
        const jwt = response.data.jwt;
        setSessionToken(jwt, getJwtExpiryDuration(jwt));
        return jwt;
    };

    // Enterprise SSO. The API resolves the identity provider from the email
    // domain and returns a one-time authorize URL on the API host; the caller
    // navigates the window there (top-level, so the API can bind the flow to
    // this browser with a cookie). 404 sso_not_available: no connection.
    const startSsoLogin = async (email, returnTo = null) => {
        const response = await api_client.post('/sso/start', { email, return_to: returnTo });
        return response.data;
    };

    // /sso/complete trades the one-time code from the callback redirect for
    // the session JWT. Single use: a second exchange of the same code is 401.
    const completeSsoLogin = async ({ code }) => {
        const response = await api_client.post('/sso/exchange', { code });
        const jwt = response.data.jwt;
        setSessionToken(jwt, getJwtExpiryDuration(jwt));
        return jwt;
    };

    // Full login flow: verify + exchange
    const login = async (options = {}) => {
        const { raw_jwt } = await verify(options);
        if (!raw_jwt) {
            console.error('Verification completed but SDK returned no raw_jwt');
            throw new Error('Verification succeeded but no user bundle was returned. Please try again.');
        }
        const session_jwt = await exchangeForSession(raw_jwt);
        return { raw_jwt, session_jwt };
    };

    // Registration for an app-provided step-up dialog (see stepUp below).
    // Returns the unregister cleanup for useEffect.
    const registerStepUpUi = useCallback(() => {
        stepUpUiHostsRef.current += 1;
        return () => { stepUpUiHostsRef.current -= 1; };
    }, []);

    // Called by the dialog once its QR container is in the DOM.
    const attachStepUpTarget = useCallback((targetId) => {
        if (stepUpTargetResolveRef.current) {
            stepUpTargetResolveRef.current(targetId);
            stepUpTargetResolveRef.current = null;
        }
    }, []);

    const cancelStepUp = useCallback(() => {
        if (stepUpCancelRef.current) stepUpCancelRef.current();
    }, []);

    // Verify for step-up: with a registered dialog host, open it and render
    // the SDK QR into its container; the cancel race lets the dialog reject
    // the pending step-up (the interceptor then surfaces the original 403).
    // Without a host (Teams/Zoom/Outlook apps) fall back to the SDK modal.
    const verifyForStepUp = async () => {
        if (!stepUpUiHostsRef.current) {
            return verify();
        }
        let cancel;
        const cancelled = new Promise((_, reject) => {
            cancel = () => reject(new Error('Step-up verification cancelled'));
        });
        const target = new Promise((resolve) => {
            stepUpTargetResolveRef.current = resolve;
        });
        stepUpCancelRef.current = cancel;
        setStepUpPromptOpen(true);
        try {
            const targetId = await Promise.race([target, cancelled]);
            return await Promise.race([verify({ targetId }), cancelled]);
        } finally {
            setStepUpPromptOpen(false);
            stepUpTargetResolveRef.current = null;
            stepUpCancelRef.current = null;
        }
    };

    // Elevate an email-assurance session to cert assurance: run the same
    // verify() QR flow login() uses, then exchange the bundle at
    // /v2/session/step_up with the current session in X-PG-Auth. Returns
    // the new session JWT so the caller (the 403 interceptor) can retry
    // the original request with it before the recreated api_client lands.
    const stepUp = async () => {
        // Single-flight: parallel step_up_required 403s share one QR dialog.
        if (stepUpPromiseRef.current) {
            return stepUpPromiseRef.current;
        }
        const run = (async () => {
            const { raw_jwt } = await verifyForStepUp();
            if (!raw_jwt) {
                throw new Error('Verification succeeded but no user bundle was returned');
            }
            const payload = { user_bundle: raw_jwt };
            if (selectedAccountId) payload.current_account_id = selectedAccountId;
            try {
                const response = await api_client.post('/v2/session/step_up', payload);
                const jwt = response.data.jwt;
                const expiryDuration = getJwtExpiryDuration(jwt);
                setSessionToken(jwt, expiryDuration);
                setOriginalUserBundle(raw_jwt, expiryDuration);
                return jwt;
            } catch (error) {
                if (error?.response?.status === 409) {
                    setStepUpConflict(true);
                }
                throw error;
            }
        })();
        stepUpPromiseRef.current = run.finally(() => {
            stepUpPromiseRef.current = null;
        });
        return stepUpPromiseRef.current;
    };
    stepUpRef.current = stepUp;

    // Cover clients created outside this provider (e.g. the MSAL tenant-sync
    // client in the console SettingsPanel): every get_api_client falls back
    // to this handler when no explicit onStepUp was passed.
    useEffect(() => setDefaultStepUpHandler(() => stepUpRef.current()), []);

    const clearStepUpConflict = useCallback(() => setStepUpConflict(false), []);


    useEffect(() => {
        console.log("Session token changed:", sessionToken);
        if (sessionToken) {
            const payload = jwtDecode(sessionToken);
            console.log("Session token payload:", payload);
            setUserName(payload.full_name || payload.poly_user_id || null);
            setAuthMethod(payload.auth_method || 'cert');
            setPolyUserId(payload.poly_user_id || null);
            const roles = payload.account_roles || {};
            setAccountRoles(roles);

            // Persist known accounts with infinite expiry (survives logout)
            if (Object.keys(roles).length > 0) {
                setKnownAccounts(roles, null);
            }

            const sessionExpiry = getJwtExpiryDuration(sessionToken);

            // Check if user needs to sign up (no accounts)
            const accountIds = Object.keys(roles);
            if (accountIds.length === 0) {
                setNeedsSignup(true);
                setSelectedAccountId(null);
            } else {
                setNeedsSignup(false);
                setSelectedAccountId(payload.current_account_id, null);  // persist across sessions
            }

            // Revalidate all SWR caches now that the session has settled.
            // api_client (and local_fetcher) have already been recomputed by
            // useMemo during this render, so SWR fetchers see the new session.
            mutate(key => true);
        }
    }, [sessionToken]);

    const isTokenExpired = (token) => {
        try {
            const { exp } = jwtDecode(token);
            return exp ? (exp * 1000) <= Date.now() : false;
        } catch (e) {
            return true;
        }
    };

    const trySilentRefresh = async () => {
        console.log("Trying silent refresh");

        // If the Polyguard session token is expired, clear it immediately
        // so the api_client is recreated without the stale X-PG-Auth header.
        if (sessionToken && isTokenExpired(sessionToken)) {
            console.warn("Polyguard session token expired, clearing session");
            logout();
        }

        let silentFailed = false;
        if (msalInstance) {
            setMsalToken(null);
            try {
                const token = await getToken(msalInstance);
                // Microsoft sometimes returns expired tokens; verify exp claim directly
                if (isTokenExpired(token)) {
                    console.warn("MSAL returned an expired token, requiring interactive login");
                    silentFailed = true;
                } else {
                    setMsalToken(token);
                    console.log("Silent refresh successful, new token is:", token);
                }
            } catch (error) {
                console.error("Error getting token:", error);
                silentFailed = true;
            }
            if (silentFailed) {
                setNeedsClick(true);
                if (sessionToken) {
                    logout();
                }
            }
        }
        if (appContext) {
            console.log("Handling zoom unauthorized");
            setNeedsZoomReauth(true);
        }
    };

    useEffect(() => {
        if (!msalInstance) {
            setMsalToken(null);
            return;
        }
        trySilentRefresh().then();
    }, [msalInstance]);

    const api_client = useMemo(() => {
        // onStepUp goes through a ref so the interceptor baked into this
        // client always calls the latest stepUp closure (which needs the
        // current session's api_client and account id).
        var client = get_api_client({ token: msalToken, sessionToken: sessionToken, appContext: appContext, accountId: zoomAccountId && zoomAccountId != 1000000000 ? zoomAccountId : null, on401: () => trySilentRefresh(), onStepUp: () => stepUpRef.current() });
        if (msalToken) {
            (async () => {
                const authResponse = await client.post('/v2/ms-teams/teams-loaded', {});
                console.dir(authResponse);
                setNeedsAccountSetup(!authResponse.data.has_account);
                setNeedsSync(authResponse.data.needs_sync);
                setHasZoomApp(authResponse.data.has_zoom_app || false);
                if (authResponse.data.account_id) {
                    setTeamsLinkedAccount({ id: authResponse.data.account_id, name: authResponse.data.account_name });
                }
            })();
        }
        if (sessionToken && msalToken) {
            (async () => {
                const syncResponse = await client.post('/ms-teams/sync-account', { poly_user_id: 'me' });
                console.dir(syncResponse);
            })();
        }
        if (appContext) {
            (async () => {
                try {
                    const zoomResponse = await client.post('/zoom-phone/zoom-loaded', {});
                    setNeedsSync(zoomResponse.data.needs_sync);
                    setZoomEnabledAccounts(zoomResponse.data.zoom_accounts || []);
                    if (zoomResponse.data.needs_reauth) {
                        setNeedsZoomReauth(true);
                    }
                } catch (err) {
                    console.error('Failed to call zoom-loaded:', err);
                }
            })();
        }
        return client;
    }, [msalToken, sessionToken, setNeedsClick, appContext, zoomAccountId]);

    const local_fetcher = useMemo(() => {
        return async (url) => {
            const response = await api_client.get(url);
            return response.data;
        };
    }, [api_client]);

    const interactiveLogin = async () => {
        if (!msalInstance) {
            throw new Error("MSAL instance not initialized");
        }
        try {
            const loginResponse = await msalInstance.acquireTokenPopup({
                scopes: scopes,
            });
            setMsalToken(loginResponse.idToken);
            console.log("Interactive login successful");
            mutate(key => true, { revalidate: true });
            setNeedsClick(false);
            return loginResponse;
        } catch (loginError) {
            console.error("Interactive login failed:", loginError);
            throw loginError;
        }
    };


    const changeAccount = useCallback(async (targetAccountId) => {
        if (!api_client) {
            console.error('API client not initialized');
            return false;
        }
        console.log("Changing account to:", targetAccountId);
        // Preferred path for every session type: re-mint the current session
        // scoped to the target account. Preserves auth_method and expiry, and
        // works when the original verification bundle has expired (or never
        // existed — email sessions have no bundle).
        if (sessionToken && !isTokenExpired(sessionToken)) {
            try {
                const response = await api_client.post('/v2/session/switch_account', { account_id: targetAccountId });
                setSessionToken(response.data.jwt, getJwtExpiryDuration(response.data.jwt));
                return true;
            } catch (error) {
                console.error('Failed to switch account via session, falling back to bundle re-exchange:', error);
            }
        }
        if (!originalUserBundle) {
            console.error('No session or user bundle available for account change');
            return false;
        }
        try {
            await exchangeForSession(originalUserBundle, targetAccountId);
            return true;
        } catch (error) {
            console.error('Failed to change account:', error);
            return false;
        }
    }, [sessionToken, originalUserBundle, api_client, setSessionToken, mutate ]);


    const value = {
        msalInstance,
        msalToken,
        appContext,
        accountId: selectedAccountId || null,
        zoomAccountId,
        setZoomAccountId,
        meetingUUID,
        api_client,
        isAuthenticated: !!msalToken || !!sessionToken,
        setNeedsClick,
        local_fetcher,
        changeAccount,
        userName,
        accountRoles: accountRoles || {},
        needsSignup,
        needsSync,
        zoomEnabledAccounts,
        hasZoomApp,
        needsZoomReauth,
        setNeedsZoomReauth,
        teamsLinkedAccount,
        logout,
        verify,
        exchangeForSession,
        login,
        requestEmailLogin,
        verifyEmailLogin,
        startSsoLogin,
        completeSsoLogin,
        verifyTotpChallenge,
        stepUp,
        stepUpConflict,
        clearStepUpConflict,
        stepUpPromptOpen,
        registerStepUpUi,
        attachStepUpTarget,
        cancelStepUp,
        authMethod,
        dataSubject,
        polyUserId,
        // True when the session subject is an email-only identity not yet
        // adopted under a cert-backed Polyguard identity (the reserved
        // "email-x" prefix). Drives the "link your Polyguard identity" prompts.
        isEmailOnlyIdentity: !!polyUserId && polyUserId.startsWith('email-x'),
        verificationJWT,
        knownAccounts,
    };

    return (
        <PolyAuthContext.Provider value={value}>
            {needsSync && requireAccount && !needsAccountSetup ?
                        <LoginPanel /> :
            needsAccountSetup === true && requireAccount ?
                <div style={{ margin: 20 }}>Your Microsoft organization has not created a Polyguard Account yet. Please ask your administrator to sign up at <a href="https://console.polyguard.ai/signup" target="_blank" rel="noreferrer">https://console.polyguard.ai/signup</a> to continue.</div> :
                needsClick && !allowAnonymous ?
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%' }}><Button style={{ margin: 20 }} appearance="primary" onClick={() => interactiveLogin()}>Authorize the Polyguard Teams App</Button></div> :
                    api_client ? children : <Spinner style={{ margin: 100 }} />}
        </PolyAuthContext.Provider>
    );
};

export const usePolyAuth = () => {
    const context = useContext(PolyAuthContext);
    if (context === null) {
        throw new Error('usePolyAuth must be used within a PolyAuthContextProvider');
    }
    return context;
};
